import { useEffect, useState } from "react";
import { ApiError, CurrentUser, getCurrentUser } from "../lib/api";

/**
 * Wraps the app's routes. On first paint it probes /api/auth/me to figure
 * out whether the visitor is signed in:
 *
 *   - 200 → renders children, hands them the user via context (TODO: add
 *           a UserContext if components other than the header end up
 *           needing the user; for now only the header reads it, and we
 *           re-fetch there).
 *   - 401 → renders <SignInPrompt /> with the "Sign in with ORCID" button
 *           that bounces to the Cognito hosted UI.
 *   - 403 → renders <NotAuthorized /> — the ORCID iD is valid but not yet
 *           in `allowed_orcids`. Includes the operator's contact line.
 *
 * Dev-mode quirk: in `AUTH_MODE=dev` the backend always returns 200 for
 * /api/auth/me (the dev user), so this component is effectively a
 * pass-through on localhost.
 */
export default function AuthGate(props: { children: React.ReactNode }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; user: CurrentUser }
    | { kind: "signed_out" }
    | { kind: "not_authorized"; orcid: string | null }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await getCurrentUser();
        if (!cancelled) setState({ kind: "ok", user });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          setState({ kind: "signed_out" });
        } else if (e instanceof ApiError && e.status === 403) {
          const detail = (e.body as { detail?: string })?.detail ?? "";
          const m = detail.match(/(\d{4}-\d{4}-\d{4}-\d{3}[0-9X])/);
          setState({ kind: "not_authorized", orcid: m ? m[1] : null });
        } else {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-500">
        Checking sign-in…
      </div>
    );
  }
  if (state.kind === "signed_out") return <SignInPrompt />;
  if (state.kind === "not_authorized")
    return <NotAuthorized orcid={state.orcid} />;
  if (state.kind === "error") {
    return (
      <div className="m-8 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        Couldn't reach the auth service: {state.message}. Try reloading.
      </div>
    );
  }
  // OK — render the app.
  return <>{props.children}</>;
}

function SignInPrompt() {
  // The hosted-UI URL is injected at deploy time via a small /api/auth/config
  // endpoint, OR — simpler for v1 — we just hardcode `/api/auth/signin` as
  // a backend route that 302s to Cognito. We take the second path here.
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-md rounded border border-ink-200 bg-white p-6 text-center text-sm shadow-sm">
        <h1 className="mb-2 text-lg font-semibold text-ink-900">
          Sign in to Review Assistant
        </h1>
        <p className="mb-4 text-ink-600">
          We use your ORCID iD to sign you in. Reviewers we've invited can
          continue.
        </p>
        <a
          href="/api/auth/signin"
          className="inline-block rounded bg-emerald-700 px-4 py-2 font-semibold text-white hover:bg-emerald-800"
        >
          Sign in with ORCID
        </a>
        <p className="mt-4 text-[11px] text-ink-500">
          Don't have an ORCID iD yet?{" "}
          <a
            href="https://orcid.org/register"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-ink-700"
          >
            Register at orcid.org
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function NotAuthorized(props: { orcid: string | null }) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-lg rounded border border-amber-200 bg-amber-50 p-6 text-sm shadow-sm">
        <h1 className="mb-2 text-lg font-semibold text-amber-900">
          Your ORCID iD isn't on the invite list yet
        </h1>
        <p className="mb-2 text-amber-900">
          You signed in successfully as{" "}
          {props.orcid ? (
            <code className="font-mono">{props.orcid}</code>
          ) : (
            "an ORCID account"
          )}
          , but the operator hasn't added you to the reviewer allowlist.
        </p>
        <p className="mb-3 text-amber-900">
          If you're expecting access, send the ORCID iD above to{" "}
          <a href="mailto:rwpinder@gmail.com" className="underline">
            rwpinder@gmail.com
          </a>{" "}
          and we'll add you.
        </p>
        <a
          href="/api/auth/signout"
          className="text-xs text-amber-900 underline hover:text-amber-700"
        >
          Sign out
        </a>
      </div>
    </div>
  );
}
